import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useSelector, useDispatch } from "react-redux";
import { getUser } from "selectors/commonSelectors";
import { getAccounts } from "selectors/skedCoreSelectors";
import { openCreatePostModal } from "features/post";
import { openCreatePostModalV2 } from "../../features/post/bulk-create/bulk-create-post.actions";
import { MODAL_OPEN_FUNCTIONS } from "libs/modals/init";
import { ADD_USER_MODAL } from "features/collaboration/UserModals";
import { cn } from "@/lib/utils";
import { useTrackEvent } from "libs/analytics";
import {
  HEADER_ADD_ACCOUNT_CLICK,
  HEADER_ADD_USER_CLICK,
} from "constants/Events";
import { entitlementFeatureToggle } from "../../utils/feature-toggle-utils";
import { getSelectedAccountsForNewPost } from "pages/component/menu/menu";
import { User } from "../../features/user";
import { getOpportunityDataToUseAddUserModal } from "./AddUserMenu";
import { Z_INDEX_MODAL_BACKDROP } from "../../lib/modal-stack";
import { topNavGhostTextButtonClassName } from "./top-nav-button";

interface AddNewMenuProps {
  openSocialAccountConnectDialog: () => void;
  figmaTopNav?: boolean;
}

export function AddNewMenu({
  openSocialAccountConnectDialog,
  figmaTopNav = false,
}: AddNewMenuProps): React.ReactElement {
  const dispatch = useDispatch();
  const user: User = useSelector(getUser);
  const allAccounts = useSelector(getAccounts);
  const trackEvent = useTrackEvent();

  const isAdmin = user && user.role === "admin";
  const canAddAccounts = isAdmin || user.preferences?.canAddAccounts || false;

  const { canAddUser, acceptUsersFeature } =
    getOpportunityDataToUseAddUserModal(user);
  const canAddUsers = canAddUser && acceptUsersFeature;

  const handleNewPost = () => {
    const selectedAccountsForNewPost = getSelectedAccountsForNewPost({
      isPlannerPage: false,
      allAccounts,
      selectedAccounts: allAccounts,
    });

    if (entitlementFeatureToggle.get("bulk-create")) {
      dispatch(
        openCreatePostModalV2({
          accounts: selectedAccountsForNewPost,
        })
      );
    } else {
      dispatch(
        openCreatePostModal({
          accounts: selectedAccountsForNewPost,
        })
      );
    }
  };

  const handleNewAccount = () => {
    trackEvent({
      eventName: HEADER_ADD_ACCOUNT_CLICK,
      useServices: ["AMP"],
    });
    openSocialAccountConnectDialog();
  };

  const handleNewUser = () => {
    trackEvent({
      eventName: HEADER_ADD_USER_CLICK,
      useServices: ["AMP"],
    });
    MODAL_OPEN_FUNCTIONS[ADD_USER_MODAL](dispatch);
  };

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          className={cn(
            topNavGhostTextButtonClassName,
            figmaTopNav && "w-[114px] px-3 text-[#333333]",
            "data-[state=open]:bg-blue-50 data-[state=open]:text-blue-500"
          )}
          data-testid="add-new-menu-trigger"
        >
          <i
            className={cn(
              "fa-regular fa-circle-plus",
              figmaTopNav
                ? "flex size-5 items-center justify-center text-base"
                : "text-[18px]"
            )}
          />
          Add New
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="text-xs leading-normal font-semibold min-w-[180px] overflow-hidden rounded-lg bg-white p-1.5 shadow-lg border border-neutral-100 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:fill-mode-forwards duration-200"
          align="end"
          sideOffset={8}
          /** on top others, but below modal backdrop */
          style={{ zIndex: Z_INDEX_MODAL_BACKDROP - 1 }}
        >
          <DropdownMenu.Item
            className={cn(
              "flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-sm text-neutral-800 outline-none transition-colors",
              "hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700"
            )}
            onClick={handleNewPost}
            data-testid="add-new-post"
          >
            <i className="fa-regular fa-file-lines w-4 text-center" />
            New Post
          </DropdownMenu.Item>

          {canAddAccounts && (
            <DropdownMenu.Item
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-sm text-neutral-800 outline-none transition-colors",
                "hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700"
              )}
              onClick={handleNewAccount}
              data-testid="add-new-account"
            >
              <i className="fa-regular fa-at w-4 text-center" />
              New Account
            </DropdownMenu.Item>
          )}

          {canAddUsers && (
            <DropdownMenu.Item
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-sm text-neutral-800 outline-none transition-colors",
                "hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700"
              )}
              onClick={handleNewUser}
              data-testid="add-new-user"
            >
              <i className="fa-regular fa-user w-4 text-center" />
              New User
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
